import React, { useState, useEffect } from 'react';
import { CalculationResult } from '../utils/calculator';
import BackArrowIcon from './BackArrowIcon';
import Footer from './Footer';
import { getFirestore, collection, addDoc, serverTimestamp, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { getAuth, signInAnonymously } from 'firebase/auth';
import { generateEmailHtml } from '../utils/emailTemplate';
import { useNavigate } from 'react-router-dom';
interface ResultsPageProps {
    results: CalculationResult;
    onBack: () => void;
}

const ResultsPage: React.FC<ResultsPageProps> = ({ results, onBack }) => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        name: '',
        email: ''
    });
    const [resultIdentifier, setResultIdentifier] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isEmailSent, setIsEmailSent] = useState(false);
    const [showValidation, setShowValidation] = useState(false);
    const [nameError, setNameError] = useState<string>('');
    const [emailError, setEmailError] = useState<string>('');
    // Helper function for Hebrew year pluralization
    const getYearText = (years: number): string => {
        if (years === 0) return '';
        if (years === 1) return 'שנה';
        if (years === 2) return 'שנתיים';
        return `${years} שנים`;
    };

    // Helper function for Hebrew month pluralization
    const getMonthText = (months: number): { number: string; text: string } => {
        if (months === 0) return { number: '', text: '' };
        if (months === 1) return { number: 'חודש אחד', text: '' };
        if (months === 2) return { number: 'חודשיים', text: '' };
        return { number: `${months}`, text: ' חודשים' };
    };

    // Helper function to get intervention years with months
    const getInterventionYears = (type: 'smoking' | 'systolic' | 'cholesterol'): string => {
        const intervention = results.interventions.find(i => i.type === type);
        const years = intervention?.years || 0;
        const yearText = getYearText(Math.floor(years));
        const months = Math.round((years % 1) * 12);

        if (Math.floor(years) === 0 && months === 0) {
            return '0 שנים';
        }

        if (months > 0) {
            const monthData = getMonthText(months);
            const monthText = monthData.number + monthData.text;
            if (yearText) {
                // For 2 months (חודשיים), don't use dash after ו
                const connector = months === 2 ? 'ו' : 'ו-';
                return `${yearText} ${connector}${monthText}`;
            } else {
                return monthText;
            }
        }
        return yearText;
    };

    // Helper function to build CTA text based on available interventions
    const getCtaText = (): string => {
        const systolicIntervention = results.interventions.find(i => i.type === 'systolic');
        const cholesterolIntervention = results.interventions.find(i => i.type === 'cholesterol');
        // Only include interventions with years > 0
        const hasSystolic = systolicIntervention && (systolicIntervention.years ?? 0) > 0;
        const hasCholesterol = cholesterolIntervention && (cholesterolIntervention.years ?? 0) > 0;
        const hasSmoking = results.input_data.smoking === 'yes';
        const hasAnyIntervention = hasSystolic || hasCholesterol || hasSmoking;
        const isFemale = results.input_data.gender === 'female';
        const fillFormText = isFemale ? 'מלאי' : 'מלא';

        // If no interventions, return special message
        if (!hasAnyIntervention) {
            return `לקבלת התוצאות והמלצות לשמירה על אורח חיים בריא לתיבת המייל שלך, ${fillFormText} את הפרטים.`;
        }

        const interventions: string[] = [];

        // If we have exactly 2 interventions (systolic and cholesterol, no smoking), use "ו" instead of comma
        if (hasSystolic && hasCholesterol && !hasSmoking) {
            interventions.push('לאיזון לחץ דם וכולסטרול');
        } else {
            // Handle each intervention separately
            if (hasSystolic && hasCholesterol) {
                // This case is when we also have smoking (3 interventions)
                interventions.push('לאיזון לחץ דם, כולסטרול');
            } else if (hasSystolic) {
                interventions.push('לאיזון לחץ דם');
            } else if (hasCholesterol) {
                interventions.push('לאיזון כולסטרול');
            }

            if (hasSmoking) {
                if (interventions.length > 0) {
                    interventions.push('והפסקת עישון');
                } else {
                    interventions.push('להפסקת עישון');
                }
            }
        }

        const interventionsText = interventions.length > 0
            ? ' ' + interventions.join(' ')
            : '';

        return `לקבלת התוצאות והמלצות מותאמות אישית${interventionsText} לתיבת המייל שלך, ${fillFormText} את הפרטים.`;
    };

    // Add result to Firestore only after name and email are entered
    async function addResultToFirebase() {
        try {
            // There's also a pilotCode validation in the server but this is just in case for reduced writes/invocations to Firestore
            if (results.pilotCode !== '90210') {
                return;
            }
            // Ensure user is authenticated anonymously
            const auth = getAuth();
            if (!auth.currentUser) {
                await signInAnonymously(auth);
            }
            const db = getFirestore();
            // Get the last item in the collection sorted by createdAt
            const q = query(collection(db, "life-plus-results"), orderBy("createdAt", "desc"), limit(1));
            const querySnapshot = await getDocs(q);
            let lastItem = null;
            if (!querySnapshot.empty) {
                lastItem = querySnapshot.docs[0].data();
            }
            const lastItemIdentifier = lastItem?.resultIdentifier || 'LP000';
            const lastNumber = parseInt(lastItemIdentifier.replace('LP', ''), 10) || 0;
            const newIdentifier = `LP${String(lastNumber + 1).padStart(3, '0')}`;
            setResultIdentifier(newIdentifier);
            // Save to Firestore
            const docRef = await addDoc(collection(db, "life-plus-results"), {
                ...results,
                resultIdentifier: newIdentifier,
                createdAt: serverTimestamp()
            });
            return docRef.id;
        } catch (e) {
            console.error("Error adding document: ", e);
        }
    }

    // Send email after saving to Firestore
    async function sendMyLifePlusEmail(email: string, name: string) {
        try {
            const auth = getAuth();
            if (!auth.currentUser) {
                await signInAnonymously(auth);
            }
            const emailContent = generateEmailHtml(results, name);
            const db = getFirestore();
            await addDoc(collection(db, "mail"), {
                to: [email],
                message: {
                    subject: 'תוצאות והמלצות חיים פלוס',
                    text: ``,
                    html: `${emailContent}`,
                },
            });
        } catch (error) {
            console.error("Error adding email trigger document:", error);
        }
    }

    // Validation functions
    const validateName = (name: string): string => {
        if (!name || name.trim() === '') {
            return 'שדה השם אינו יכול להיות ריק';
        }
        // Check if name contains only letters (Hebrew, English, and spaces)
        const nameRegex = /^[\u0590-\u05FFa-zA-Z\s]+$/;
        if (!nameRegex.test(name.trim())) {
            return 'השם יכול להכיל אותיות בלבד';
        }
        return '';
    };

    const validateEmail = (email: string): string => {
        if (!email || email.trim() === '') {
            return 'שדה הדוא"ל אינו יכול להיות ריק';
        }
        // Email validation regex
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email.trim())) {
            return 'כתובת הדוא"ל אינה תקינה';
        }
        return '';
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setShowValidation(true);

        // Validate form fields
        const nameErr = validateName(formData.name);
        const emailErr = validateEmail(formData.email);

        setNameError(nameErr);
        setEmailError(emailErr);

        // If there are validation errors, don't submit
        if (nameErr || emailErr) {
            return;
        }

        setIsLoading(true);
        // await addResultToFirebase();
        await sendMyLifePlusEmail(formData.email, formData.name);
        setIsLoading(false);
        setIsEmailSent(true);
    };

    const handleInputChange = (field: string, value: string) => {
        setFormData(prev => ({ ...prev, [field]: value }));

        // Clear error when user starts typing
        if (showValidation) {
            if (field === 'name') {
                setNameError('');
            } else if (field === 'email') {
                setEmailError('');
            }
        }
    };

    useEffect(() => {
        document.title = 'תוצאות מחשבון הוספת שנות חיים - חיים פלוס';
        // Scroll to top when component mounts
        window.scrollTo(0, 0);
    }, []);

    // Check if all interventions are zero
    const systolicIntervention = results.interventions.find(i => i.type === 'systolic');
    const cholesterolIntervention = results.interventions.find(i => i.type === 'cholesterol');
    const hasSystolic = systolicIntervention && (systolicIntervention.years ?? 0) > 0;
    const hasCholesterol = cholesterolIntervention && (cholesterolIntervention.years ?? 0) > 0;
    const hasSmoking = results.input_data.smoking === 'yes';
    const hasAnyIntervention = hasSystolic || hasCholesterol || hasSmoking;
    const totalYears = Math.floor(results.total_years);
    const months = Math.round((results.total_years % 1) * 12);
    const allZero = !hasAnyIntervention && totalYears === 0 && months === 0;

    return (
        <div className="results-page">
            <a href="#main-content" className="skip-link">דלג לתוכן הראשי</a>
            <div className="container">
                {/* Back to Home Button */}
                <div className="back-home">
                    <button onClick={onBack} className="back-btn" title="חזרה למחשבון" aria-label="חזרה למחשבון">
                        <BackArrowIcon aria-hidden="true" />
                    </button>
                </div>

                {/* Main Content Container */}
                <main id="main-content" className="results-main-content">
                    {/* Header */}
                    <div className="calculator-header">
                        <div className="logo">
                            <img
                                src="/assets/life-logo.svg"
                                alt="חיים פלוס"
                            />
                        </div>
                        <img
                            src="/assets/header-img.png"
                            alt=""
                            className="header-image"
                            aria-hidden="true"
                        />
                    </div>
                    {/* Identifier Number */}
                    {/* <div className='identifier-num'>
                        {results.input_data.gender === 'female' ? 'מסרי' : 'מסור'} לרופא.ה שלך את מזהה התוצאה שלך: <span className="identifier-number">{results.resultIdentifier}</span>
                    </div> */}

                    {/* Results Card */}
                    <div className="results-card">
                        <div className={`results-content ${allZero ? 'no-gap' : ''}`}>
                            {/* Header with total years */}
                            <div className="total-years-header" role="region" aria-labelledby="total-years-heading">
                                <h2 id="total-years-heading" className="sr-only">סיכום תוצאות</h2>
                                <div className={`total-years-text ${allZero ? 'centered' : ''}`}>
                                    {(() => {
                                        // If no interventions and total is 0, show positive message
                                        if (allZero) {
                                            return (
                                                <>
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="96" height="96" viewBox="0 0 96 96" fill="none" style={{ display: 'block', margin: '0 auto 16px auto' }} aria-hidden="true">
                                                        <path d="M46.8037 87.7236L31.2637 93.8516C28.1943 95.0621 24.7117 93.546 23.501 90.4766L11.2529 59.4492L11.1484 59.1689C10.6661 57.7598 10.73 56.24 11.3271 54.8711C11.9279 53.4951 12.9871 52.4207 14.3418 51.8037L14.6162 51.6865L30.167 45.5576L46.8037 87.7236Z" fill="#6BFFBF" stroke="#333333" strokeWidth="0.08" />
                                                        <path d="M34.3599 0.15332C34.7636 -0.000253266 35.2089 0.189462 35.3657 0.591797L37.5073 6.0332C37.6609 6.43703 37.4713 6.88227 37.0688 7.03906H37.0679C36.9792 7.07447 36.8792 7.09277 36.7866 7.09277C36.4767 7.09263 36.1845 6.89986 36.0669 6.60059L33.9214 1.15918C33.7681 0.75545 33.9576 0.310072 34.3599 0.15332Z" fill="black" stroke="#333333" strokeWidth="0.08" />
                                                        <path d="M27.4756 26.501C27.8793 26.3476 28.3247 26.5371 28.4814 26.9395C28.6351 27.3433 28.4454 27.7885 28.043 27.9453L22.5039 30.1357C22.4153 30.1712 22.3153 30.1894 22.2227 30.1895C21.9507 30.1895 21.6931 30.0494 21.5518 29.8066L21.498 29.6973C21.3445 29.2935 21.5342 28.8483 21.9365 28.6914H21.9375L27.4756 26.501Z" fill="black" stroke="#333333" strokeWidth="0.08" />
                                                        <path d="M62.729 12.6035C63.1078 12.4595 63.5232 12.6175 63.7026 12.9697L63.7349 13.042C63.8789 13.4206 63.7215 13.836 63.3696 14.0156L63.2964 14.0479L57.9487 16.1592C57.8601 16.1946 57.7601 16.2128 57.6675 16.2129C57.3568 16.2129 57.0641 16.0308 56.9429 15.7207C56.7895 15.317 56.979 14.8716 57.3813 14.7148L62.729 12.6035Z" fill="black" stroke="#333333" strokeWidth="0.08" />
                                                        <path d="M50.7554 0.890625C50.9305 0.497566 51.3874 0.312164 51.7788 0.486328C52.1719 0.66144 52.3564 1.11833 52.1821 1.50977V1.51074L49.8569 6.8584C49.7284 7.15058 49.4434 7.3252 49.144 7.3252C49.0325 7.32519 48.9332 7.30758 48.8345 7.26172V7.26074C48.4413 7.0856 48.2558 6.62879 48.4302 6.2373H48.4312L50.7554 0.890625Z" fill="black" stroke="#333333" strokeWidth="0.08" />
                                                        <path d="M21.8574 12.2759C22.0325 11.8829 22.4895 11.6976 22.8809 11.8716L22.8818 11.8726L28.2285 14.1968C28.6217 14.3719 28.8062 14.8287 28.6318 15.2202C28.5034 15.5127 28.2185 15.6879 27.9189 15.688C27.8074 15.688 27.7081 15.6703 27.6094 15.6245L27.6084 15.6235L22.2607 13.2983C21.8774 13.135 21.694 12.68 21.8574 12.2759Z" fill="black" stroke="#333333" strokeWidth="0.08" />
                                                        <path d="M38.5156 11.7188C39.9554 11.1556 41.5035 11.0513 43.0029 11.4131C44.5095 11.7823 45.8369 12.6063 46.8213 13.7881V13.7891C48.4176 15.7033 49.4723 18.018 49.9512 20.6816C50.4305 23.3853 50.5057 26.483 50.5732 29.2285C50.6107 30.7637 50.6537 32.4911 50.7969 33.9062C50.8684 34.6134 50.9656 35.2442 51.0996 35.7354C51.1666 35.9808 51.2429 36.193 51.3311 36.3623C51.4182 36.5296 51.5191 36.6577 51.6348 36.7354V36.7363C51.6589 36.7557 51.7094 36.7891 51.8047 36.7959C51.8988 36.8025 52.0347 36.7834 52.2295 36.7051L52.2305 36.7041L70.7217 28.458C70.7244 28.4572 70.7273 28.457 70.7295 28.4561L70.7412 28.4502H70.7432L70.751 28.4473C72.6496 27.6939 74.7123 27.7307 76.585 28.5439C78.4577 29.3572 79.8974 30.846 80.6436 32.7373C81.9119 35.9718 80.845 39.6124 78.1035 41.6455L78.0742 41.667L78.0928 41.6982L78.1035 41.7178L78.1123 41.7314L78.1289 41.7354C80.5536 42.3061 82.523 44.0075 83.4482 46.3467C84.3846 48.7231 84.0788 51.3494 82.665 53.4121L82.6396 53.4482L82.6797 53.4697C84.2838 54.3203 85.5442 55.7041 86.2119 57.4121C87.111 59.7064 86.8615 62.2286 85.5635 64.2803L85.5498 64.3027L85.5635 64.3242C85.8873 64.8119 86.1522 65.344 86.3535 65.9062V65.9072L86.3545 65.9092V65.9102C87.7037 69.628 85.8383 73.8461 82.207 75.4863L81.8506 75.6367C81.8366 75.642 81.8235 75.6467 81.8096 75.6514C81.7964 75.6557 81.7823 75.6606 81.7676 75.666L47.583 85.0557L47.54 85.0684L47.5566 85.1094L48.6592 87.9033C48.8124 88.307 48.6229 88.7514 48.2207 88.9082V88.9092L31.8711 95.3584C30.9534 95.7203 30.0051 95.8926 29.0801 95.8926C26.0436 95.8924 23.1678 94.0562 21.9854 91.0684L9.74512 60.042V60.041C8.99175 58.1499 9.02869 56.0761 9.8418 54.2148C10.6551 52.3533 12.1438 50.9131 14.0352 50.167L30.3965 43.7207V43.7217C30.586 43.6502 30.7966 43.6494 30.9893 43.7314C31.1817 43.8134 31.3273 43.9671 31.3984 44.1592V44.1602L32.291 46.417L32.3262 46.5049L32.3652 46.4189C33.8626 43.0973 35.7282 37.8684 35.9385 32.1201C36.0737 28.3055 35.5062 24.5653 34.2559 20.998C32.9394 17.2576 34.8118 13.1809 38.5156 11.7188ZM30.1758 45.4697L14.5869 51.6123C13.0809 52.2124 11.9031 53.3527 11.2539 54.8398C10.6085 56.3193 10.5778 57.9725 11.1777 59.4785V59.4795L23.4258 90.5059C24.6526 93.6163 28.1824 95.1534 31.293 93.9268L46.8701 87.7842L46.9072 87.7695L46.8926 87.7324L30.2275 45.4922L30.2129 45.4551L30.1758 45.4697ZM42.6162 12.9111C41.4272 12.6205 40.1998 12.7002 39.0596 13.1533C36.13 14.3125 34.6531 17.5294 35.6953 20.4785C37.0099 24.2269 37.6023 28.1595 37.46 32.167C37.2127 39.062 34.6796 45.2096 33.0869 48.4512L33.0791 48.4678L33.0859 48.4834L46.9424 83.6025L46.9561 83.6357L46.9902 83.626L81.2998 74.1992L81.3037 74.1973C84.394 72.9521 86.0066 69.4845 84.8926 66.4258C84.7043 65.9006 84.4432 65.4074 84.123 64.9609L83.9824 64.7725C83.7719 64.4974 83.7616 64.1123 83.9717 63.8311C85.2362 62.1287 85.5267 59.9352 84.7529 57.9648C84.1037 56.3341 82.7821 55.0654 81.1289 54.4766L81.0391 54.4404C80.8393 54.344 80.6929 54.1633 80.6367 53.9385C80.5807 53.714 80.6287 53.484 80.7588 53.3008L80.8193 53.2256C82.4046 51.508 82.8499 49.0804 81.9893 46.9062C81.2042 44.9247 79.4863 43.5244 77.3877 43.1582C77.1569 43.1177 76.9586 42.9729 76.8457 42.7725L76.8027 42.6836L76.3906 41.6484C76.2554 41.2992 76.3907 40.9004 76.7109 40.708H76.7119C79.2336 39.1715 80.2679 36.0601 79.1846 33.3047C78.5957 31.7949 77.4439 30.6101 75.957 29.9609C74.4811 29.3155 72.8351 29.2853 71.3291 29.874H71.3271L52.8359 38.1318C52.8333 38.1326 52.8303 38.1329 52.8281 38.1338C52.8229 38.1361 52.8183 38.1385 52.8164 38.1396H52.8145L52.8066 38.1426C51.8418 38.5306 51.1658 38.2938 50.7705 38.0303C49.9985 37.5175 49.5849 36.4583 49.3535 34.9531C49.1224 33.4502 49.075 31.5157 49.0225 29.2686C48.955 26.5847 48.8798 23.5368 48.418 20.9609V20.96C48.0126 18.7113 47.1574 16.7424 45.877 15.1006L45.6152 14.7773C44.8483 13.848 43.8056 13.2019 42.6162 12.9111Z" fill="black" stroke="#333333" strokeWidth="0.08" />
                                                        <path d="M28.1672 81.0043C27.6122 82.2793 27.5897 83.7005 28.0997 84.9981C28.906 87.0531 30.8822 88.3018 32.956 88.3018C33.5897 88.3018 34.2422 88.1856 34.8647 87.9381C36.1622 87.4281 37.1785 86.4418 37.7334 85.163C38.2884 83.888 38.3109 82.4668 37.8009 81.1693C37.2909 79.8717 36.3047 78.8555 35.0259 78.3005C33.7508 77.7455 32.3296 77.723 31.0321 78.233C29.7421 78.7393 28.7222 79.718 28.1672 81.0043Z" fill="#E3FAFF" />
                                                        <path d="M31.0324 78.2334C32.3299 77.7234 33.7511 77.7451 35.0261 78.3001C36.3048 78.855 37.2911 79.8715 37.8011 81.1688C38.3111 82.4662 38.2883 83.8876 37.7334 85.1626C37.1785 86.4414 36.1622 87.4286 34.8647 87.9386C34.2424 88.186 33.5899 88.3021 32.9564 88.3022V87.2355C33.4608 87.2355 33.9793 87.1424 34.4709 86.947L34.4741 86.9449C35.5059 86.5393 36.3124 85.7592 36.7553 84.7386V84.7376C37.1966 83.7238 37.2158 82.5958 36.8084 81.5595C36.4028 80.5276 35.6218 79.7222 34.6011 79.2792L34.6001 79.2782C33.6498 78.8646 32.5992 78.8224 31.6178 79.1542L31.422 79.2261C30.3953 79.629 29.5887 80.4034 29.147 81.4272L29.1449 81.4303C28.7313 82.3806 28.689 83.4312 29.0209 84.4126L29.0928 84.6084L29.223 84.9063C29.9283 86.3588 31.406 87.2355 32.9564 87.2355V88.3022L32.5689 88.2876C30.64 88.143 28.856 86.9247 28.1001 84.998C27.5901 83.7005 27.6118 82.2793 28.1668 81.0042C28.6871 79.7982 29.6167 78.8628 30.7939 78.3334L31.0324 78.2334Z" fill="#333333" />
                                                    </svg>
                                                    <span className="text-regular">כל הכבוד! המדדים שלך תקינים.</span>
                                                </>
                                            );
                                        }

                                        // Otherwise show the normal total years display
                                        const hasYears = totalYears > 0;
                                        const hasMonths = months > 0;

                                        return (
                                            <>
                                                <span className="text-regular">רווח השנים הפוטנציאלי:</span>
                                                <span className="text-bold"> </span>
                                                {hasYears && hasMonths ? (
                                                    <>
                                                        {(() => {
                                                            const monthData = getMonthText(months);
                                                            const connector = months === 2 ? 'ו' : 'ו-';
                                                            return (
                                                                <>
                                                                    <span className="text-bold text-blue">{getYearText(totalYears)}</span>
                                                                    <span className="text-bold"> {connector}</span>
                                                                    <span className="text-bold text-blue">{monthData.number}</span>
                                                                    {monthData.text && <span className="text-bold">{monthData.text}</span>}
                                                                </>
                                                            );
                                                        })()}
                                                    </>
                                                ) : hasYears ? (
                                                    <span className="text-bold text-blue">{getYearText(totalYears)}</span>
                                                ) : hasMonths ? (
                                                    <>
                                                        {(() => {
                                                            const monthData = getMonthText(months);
                                                            return (
                                                                <>
                                                                    <span className="text-bold text-blue">{monthData.number}</span>
                                                                    {monthData.text && <span className="text-bold">{monthData.text}</span>}
                                                                </>
                                                            );
                                                        })()}
                                                    </>
                                                ) : (
                                                    <span className="text-bold text-blue">0 שנים</span>
                                                )}
                                            </>
                                        );
                                    })()}
                                </div>
                            </div>

                            {/* Interventions breakdown */}
                            <div className="interventions-breakdown" role="list" aria-label="פירוט התערבויות">
                                {(() => {
                                    const visibleItems: Array<'systolic' | 'cholesterol' | 'smoking'> = [];

                                    if (hasSystolic) visibleItems.push('systolic');
                                    if (hasCholesterol) visibleItems.push('cholesterol');
                                    if (hasSmoking) visibleItems.push('smoking');

                                    return visibleItems.map((item, index) => {
                                        const isNotFirst = index > 0;
                                        return (
                                            <React.Fragment key={item}>
                                                {isNotFirst && <div className="separator"></div>}
                                                {item === 'systolic' && (
                                                    <div className="results-intervention-item" role="listitem">
                                                        <div className="results-intervention-content">
                                                            <div className="results-intervention-title">לחץ דם סיסטולי</div>
                                                            <div className="results-intervention-description">
                                                                <span>רווח השנים הפוטנציאלי לאחר איזון לחץ הדם: </span>
                                                                <span className="text-bold text-blue">{getInterventionYears('systolic')}</span>
                                                            </div>
                                                        </div>
                                                        <div className="results-intervention-icon">
                                                            <img
                                                                src="/assets/blood-pressure-icon.svg"
                                                                alt=""
                                                                className="results-intervention-svg"
                                                                aria-hidden="true"
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                                {item === 'cholesterol' && (
                                                    <div className="results-intervention-item" role="listitem">
                                                        <div className="results-intervention-content">
                                                            <div className="results-intervention-title">כולסטרול (Non-HDL)</div>
                                                            <div className="results-intervention-description">
                                                                <span>רווח השנים הפוטנציאלי לאחר איזון רמות הכולסטרול: </span>
                                                                <span className="text-bold text-blue">{getInterventionYears('cholesterol')}</span>
                                                            </div>
                                                        </div>
                                                        <div className="results-intervention-icon">
                                                            <img
                                                                src="/assets/cholesterol-icon.svg"
                                                                alt=""
                                                                className="results-intervention-svg"
                                                                aria-hidden="true"
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                                {item === 'smoking' && (
                                                    <div className="results-intervention-item" role="listitem">
                                                        <div className="results-intervention-content">
                                                            <div className="results-intervention-title">עישון</div>
                                                            <div className="results-intervention-description">
                                                                <span>רווח השנים הפוטנציאלי לאחר הפסקת עישון: </span>
                                                                <span className="text-bold text-blue">{getInterventionYears('smoking')}</span>
                                                            </div>
                                                        </div>
                                                        <div className="results-intervention-icon">
                                                            <img
                                                                src="/assets/smoking-icon.svg"
                                                                alt=""
                                                                className="results-intervention-svg"
                                                                aria-hidden="true"
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                            </React.Fragment>
                                        );
                                    });
                                })()}
                            </div>
                        </div>
                    </div>

                    {/* Illustrations Section */}
                    <div className="illustrations-section">
                        {(() => {
                            if (!allZero) {
                                return (
                                    <>
                                        <p className="illustrations-text">
                                            {results.input_data.gender === 'female'
                                                ? 'עצרי לרגע ותדמייני מה תוכלי להספיק בשנים הבריאות הנוספות האלו...'
                                                : 'עצור לרגע ותדמיין מה תוכל להספיק בשנים הבריאות הנוספות האלו...'
                                            }
                                        </p>
                                        <div className="illustrations-container">
                                            <div className="illustration-item">
                                                <img src="/assets/res-1.png" alt="" className="illustration-image" aria-hidden="true" />
                                            </div>
                                            <div className="illustration-item">
                                                <img src="/assets/res-2.png" alt="" className="illustration-image" aria-hidden="true" />
                                            </div>
                                            <div className="illustration-item">
                                                <img src="/assets/res-3.png" alt="" className="illustration-image" aria-hidden="true" />
                                            </div>
                                        </div>
                                    </>
                                );
                            }
                            return null;
                        })()}

                        {/* Call to Action Form */}
                        <div className="cta-section">
                            <div className="cta-text">
                                {getCtaText()}
                            </div>

                            <form onSubmit={handleSubmit} className="cta-form" noValidate>
                                {/* ARIA live region for form status */}
                                <div aria-live="polite" aria-atomic="true" className="sr-only">
                                    {isLoading && 'שולח מייל, אנא המתן'}
                                    {isEmailSent && 'המייל נשלח בהצלחה'}
                                </div>
                                {!isEmailSent ? (
                                    <div className="form-row">
                                        <div className="form-field">
                                            <label className="results-form-label" htmlFor="results-name-input">שם</label>
                                            <input
                                                id="results-name-input"
                                                type="text"
                                                value={formData.name}
                                                onChange={(e) => handleInputChange('name', e.target.value)}
                                                className={`results-form-input ${nameError ? 'error' : ''}`}
                                                placeholder=""
                                                aria-required="true"
                                                aria-invalid={nameError ? true : false}
                                                aria-describedby={nameError ? 'name-error' : undefined}
                                            />
                                            {nameError && (
                                                <div className="error-message" id="name-error" role="alert">
                                                    {nameError}
                                                </div>
                                            )}
                                        </div>
                                        <div className="form-field">
                                            <label className="results-form-label" htmlFor="results-email-input">דואר אלקטרוני</label>
                                            <input
                                                id="results-email-input"
                                                type="text"
                                                inputMode="email"
                                                value={formData.email}
                                                onChange={(e) => handleInputChange('email', e.target.value)}
                                                className={`results-form-input email ${emailError ? 'error' : ''}`}
                                                placeholder=""
                                                aria-required="true"
                                                aria-invalid={emailError ? true : false}
                                                aria-describedby={emailError ? 'email-error' : undefined}
                                            />
                                            {emailError && (
                                                <div className="error-message" id="email-error" role="alert">
                                                    {emailError}
                                                </div>
                                            )}
                                        </div>
                                    </div>) : (
                                    <div className="form-row">
                                        <div className="form-field" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                            <div className="results-form-label" role="status" aria-live="polite" style={{ margin: 0 }}>תודה! המייל נשלח בהצלחה.</div>
                                            <button
                                                type="button"
                                                onClick={() => navigate('/')}
                                                className="back-to-home-btn"
                                                style={{
                                                    background: 'transparent',
                                                    border: 'none',
                                                    color: '#006574',
                                                    cursor: 'pointer',
                                                    fontSize: '16px',
                                                    textDecoration: 'underline',
                                                    padding: 0
                                                }}
                                            >
                                                חזרה
                                            </button>
                                        </div>
                                    </div>
                                )}

                                <button type="submit" className="submit-btn" disabled={isLoading || isEmailSent}
                                    style={isEmailSent ? { display: 'none' } : {}}
                                    aria-busy={isLoading}
                                    aria-disabled={isLoading || isEmailSent}>
                                    {isLoading ? (
                                        <>
                                            <span className="loading-spinner" aria-hidden="true"></span>
                                            שולח...
                                        </>
                                    ) : isEmailSent ? (
                                        'נשלח בהצלחה'
                                    ) : (
                                        'שליחה'
                                    )}
                                </button>
                            </form>
                        </div>

                        {results.resultIdentifier && (<div className='identifier-num'>
                            {results.input_data.gender === 'female' ? 'מסרי' : 'מסור'} לרופא.ה שלך את מזהה התוצאה שלך: <span className="identifier-number">{results.resultIdentifierDisplay}</span>
                        </div>)}
                    </div>

                    <p className="data-disclaimer">
                        הנתונים לא ישמרו במערכת וימחקו לאחר שליחת דוא"ל חד פעמי.
                    </p>

                    {/* Footer Disclaimer */}
                    <Footer />
                </main>
            </div>
        </div>
    );
};

export default ResultsPage;
